import { PublicToken, User } from '@/definitions';
import {
  CheckRegistrationValid,
  FirebaseCustomToken,
  FirebaseCustomTokenResponse,
} from '@/definitions/Auth';
import { http } from '@/lib/http';
import {
  checkRegistrationAdapter,
  firebaseCustomTokenAdapter,
  userAdapter,
  userInfoAdapter,
} from './adapter';
import {
  CheckEmailRegistrationResponse,
  FirebaseTokenRequest,
  FirebaseTokenResponse,
  FlagsResponse,
  ProfileResponse,
  RegisterUserDTO,
  UserApiResponse,
  UserInfo,
  UserSettingUpdate,
  UsersResponse,
} from './types';

async function getUserInfo(uuid: string): Promise<UserInfo[]> {
  const response = await http.get<UsersResponse>(`/api/v1/history/users/${uuid}`);

  return response.users.map(userInfoAdapter);
}

async function getUser(): Promise<User> {
  const response = await http.get<UserApiResponse>('/api/v1/accounts/profile/bootstrap', {
    next: {
      tags: ['user'],
    },
  });

  return userAdapter(response);
}

async function updateUserSettings(settings: UserSettingUpdate[]): Promise<void> {
  await http.patch('/api/v1/accounts/profile/settings', settings);
}

function signIn(data: RegisterUserDTO): Promise<void> {
  return http.post('/api/v1/auth/sign-in', null, {
    headers: {
      authorization: `Bearer ${data.token}`,
    },
  });
}

function getFirebaseToken(data: FirebaseTokenRequest): Promise<FirebaseTokenResponse> {
  return http.post<FirebaseTokenResponse>('/debug/firebase-token', data);
}

function signUp(data: RegisterUserDTO): Promise<void> {
  return http.post('/api/v1/auth/sign-up', null, {
    headers: {
      authorization: `Bearer ${data.token}`,
    },
  });
}

function authFederated(data: RegisterUserDTO): Promise<void> {
  return http.post('/api/v1/auth/federated-identity', null, {
    headers: {
      authorization: `Bearer ${data.token}`,
    },
  });
}

async function isValidEmailToRegister(email: string): Promise<CheckRegistrationValid> {
  const response = await http.post<CheckEmailRegistrationResponse>(
    `/api/v1/auth/validate-patient`,
    {
      email,
    },
  );

  return checkRegistrationAdapter(response);
}

function forgotPassword(email: string): Promise<void> {
  return http.post(`/api/v1/auth/forgot-password`, {
    email,
  });
}

function resetPassword(email: string): Promise<void> {
  return http.post(`/api/v1/auth/reset-password`, {
    email,
  });
}

function sessionCookie({ token }: { token: string }) {
  return http.post(`/api/v1/auth/session-cookie`, {
    auth_token: token,
  });
}

function getProfile(): Promise<ProfileResponse> {
  return http.get<ProfileResponse>('/api/v1/accounts/profile', {
    cache: 'no-store',
  });
}

function signOut(): Promise<void> {
  return http.post('/api/v1/auth/sign-out', null);
}

async function checkLogin(): Promise<boolean> {
  try {
    await getProfile();

    return true;
  } catch {
    return false;
  }
}

async function getFlags(): Promise<FlagsResponse> {
  const response = await http.get<FlagsResponse>('/api/v1/accounts/profile/flags', {
    cache: 'force-cache',
    revalidate: 900,
  });

  return response;
}

async function externalLogin(email: string): Promise<PublicToken> {
  const response = await http.post<PublicToken>(
    '/api/v1/integrations/techbalance/auth/public-token',
    {
      email,
    },
  );

  return response;
}

async function getFirebaseCustomToken({
  token,
  email,
}: {
  token: string;
  email: string;
}): Promise<FirebaseCustomToken> {
  const response = await http.post<FirebaseCustomTokenResponse>(
    '/api/v1/integrations/techbalance/auth/firebase-custom-token',
    {
      token,
      email,
    },
  );

  return firebaseCustomTokenAdapter(response);
}

export {
  authFederated,
  checkLogin,
  externalLogin,
  forgotPassword,
  getFirebaseCustomToken,
  getFlags,
  getProfile,
  getUser,
  getUserInfo,
  isValidEmailToRegister,
  resetPassword,
  sessionCookie,
  signIn,
  signOut,
  signUp,
  updateUserSettings,
  getFirebaseToken,
};
